import {AfterViewInit, Component, inject, OnInit} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {Store} from '@ngxs/store';
import {LogoutUser} from '../../state/user.actions';
import {Helper} from '../../helper/helper';
import {environment} from '../../../../environments/environment';
import Role from '../../enums/role.enum';
import {SubscriptionService} from '../../../core/services/subscription.service';

@Component({
    selector: 'app-side-nav',
    templateUrl: './side-nav.component.html',
    styleUrls: ['./side-nav.component.scss']
})
export class SideNavComponent implements OnInit, AfterViewInit {
    sidenav: MenuItem[];
    langSwitch = 'de';
    langSwitchUri = '';
    currentLang = '';
    env = environment;
    date = new Date();
    navBarToggledClass = false;

    $currentUser = inject(Store).select(state => state.currentUser);
    private subscriptionService = inject(SubscriptionService);

    // Status für das Abonnement hinzufügen
    subscriptionStatus$ = this.subscriptionService.hasSubscription$;


    constructor(
        private store: Store,
        public helper: Helper,
    ) {
        const uri = window.location.pathname.split('/');
        this.langSwitchUri = uri.join('/');
        switch (uri[1]) {
            case 'de':
                this.currentLang = '/de';
                uri[1] = 'en';
                this.langSwitchUri = uri.join('/');
                this.langSwitch = 'en';
                break;
            case 'en':
                this.currentLang = '/en';
                uri[1] = 'de';
                this.langSwitchUri = uri.join('/');
                this.langSwitch = 'de';
                break;
            default:
                this.currentLang = '';
                this.langSwitch = 'de';
        }
    }

    /**
     * Führt die command-Funktion eines MenuItem-Elements aus, falls vorhanden.
     * Diese Methode ermöglicht es, dass MenuItems eigene Click-Handler haben können,
     * ohne dass das Template für jede spezifische Aktion angepasst werden muss.
     *
     * @param element Das MenuItem-Element, dessen command-Funktion ausgeführt werden soll
     */
    executeCommand(element: any): void {
        if (element.command) {
            element.command();
        }
    }

    /**
     * Ermittelt das anzuzeigende Label für ein MenuItem-Element.
     * Unterstützt sowohl statische Labels als auch dynamische Labels,
     * die zur Laufzeit basierend auf dem aktuellen Zustand generiert werden.
     *
     * @param element Das MenuItem-Element
     * @returns Das statische Label oder das Ergebnis der dynamicLabel-Funktion
     */
    getElementLabel(element: any): string {
        if (element.dynamicLabel) {
            return element.dynamicLabel();
        }
        return element.label;
    }

    /**
     * Ermittelt die CSS-Klassen für ein MenuItem-Element.
     * Kombiniert statische styleClass als Basis mit optionalen dynamischen Klassen.
     *
     * @param element Das MenuItem-Element
     * @returns Die kombinierten CSS-Klassen
     */
    getElementClass(element: any): string {
        let classes = element.styleClass || '';
        if (element.dynamicClass) {
            const dynamicClasses = element.dynamicClass();
            if (dynamicClasses) {
                classes = classes ? `${classes} ${dynamicClasses}` : dynamicClasses;
            }
        }
        return classes;
    }


    // Links
    //  https://www.lobster-event.com/de/info-guide/8607e6e9-8006-3542-a947-83375c8aa616?mode=preview (Buyer de)
    // https://www.lobster-event.com/de/info-guide/7413a4e2-ebc3-396d-99c3-65db38849fdd?mode=preview (Buyer en)
    // https://www.lobster-event.com/de/info-guide/1ec5ad3c-4482-35d5-bc25-371f33a93ed5?mode=preview (Supplier en)

    public showHideSideNav() {
        this.navBarToggledClass = !this.navBarToggledClass;
        if (this.navBarToggledClass) {
            document.body.classList.add('sidenav-open');
        } else {
            document.body.classList.remove('sidenav-open');
        }
    }

    ngOnInit(): void {
        this.sidenav = [];
    }

    logout(): void {
        this.store.dispatch(new LogoutUser());
    }

    async ngAfterViewInit(): Promise<void> {
        const activeContracts: boolean = await this.subscriptionService.hasActiveContracts();
        // Wenn Buyer
        if (this.helper.hasRole(Role.BUYER)) {
            this.sidenav.push({
                label: 'Navigation',
                items: [
                    {
                        label: $localize`:@@navigation.start:Start`,
                        icon: 'fa-light fa-border-all',
                        routerLink: this.currentLang + '/dashboard'
                    },
                    {
                        label: 'Suppliers',
                        //label:  $localize`:@@navigation.overview:Übersicht`,
                        icon: 'fa-light fa-map-location-dot',
                        routerLink: this.currentLang + '/directory/s/overview'
                    },
                    {
                        label: $localize`:@@fam-trips:FAM Trips`,
                        icon: 'fa-light fa-plane-departure fa-fw',
                        routerLink: this.currentLang + '/fam-trips/b/overview'
                    }
                ]
            });
        }

        // Wenn Supplier
        if (this.helper.hasRole(Role.SUPPLIER)) {
            this.sidenav.push({
                label: 'Navigation',
                items: [
                    {
                        label: $localize`:@@navigation.start:Start`,
                        icon: 'fa-light fa-border-all',
                        routerLink: this.currentLang + '/dashboard'
                    },
                    {
                        label: 'Buyers',
                        //label:  $localize`:@@navigation.overview:Übersicht`,
                        icon: 'fa-light fa-map-location-dot',
                        routerLink: this.currentLang + '/directory/b/overview'
                    }
                ]
            });
        }

        // Wenn Supplier
        if (this.helper.hasRole(Role.SUPPLIER)) {
            const items = [
                {
                    label: $localize`:@@navigation.my.account:Mein Account`,
                    icon: 'fa-light fa-address-card fa-fw',
                    routerLink: this.currentLang + '/account'
                }
            ];

            if (!this.helper.isReadOnly()) {
                items.push(
                    {
                        label: $localize`:@@myproducts:Meine Standorte`,
                        icon: 'fa-light fa-house-chimney-user fa-fw',
                        routerLink: this.currentLang + '/account/products'
                    },
                    {
                        label: $localize`:@@fam-trips:FAM Trips`,
                        icon: 'fa-light fa-plane-departure fa-fw',
                        routerLink: this.currentLang + '/fam-trips/s/overview'
                    }
                );
            }
            if (this.helper.isMasterAccount()) {
                items.push({
                    label: $localize`:@@user.subaccounts:Mitbenutzer`,
                    icon: 'fa-light fa-users fa-fw',
                    routerLink: this.currentLang + '/account/users'
                });
            }
            if (!activeContracts && this.helper.isMasterAccount()) {
                items.push({
                    label: $localize`:@@mysubscription:Jetzt Abo aktivieren!`,
                    icon: 'fa-light fa-gem fa-fw',
                    routerLink: this.currentLang + '/subscription/booking'
                });
            }

            items.push(
                {
                    label: 'Status',
                    icon: 'fa-light fa-gem fa-fw',
                    // @ts-ignore
                    styleClass: 'subscription-toggle',
                    dynamicClass: () => `subscription-toggle ${this.subscriptionService.hasSubscription ? 'active' : 'inactive'}`
                }
            )


            this.sidenav.push({
                label: $localize`:@@navigation.account:Account`,
                icon: 'fal fa-fw fa-user',
                items
            });
        }
        // Wenn Buyer
        if (this.helper.hasRole(Role.BUYER)) {
            const items = [
                {
                    label: $localize`:@@navigation.my.account:Mein Account`,
                    icon: 'fa-light fa-address-card fa-fw',
                    routerLink: this.currentLang + '/account'
                }
            ];
            if (!this.helper.isReadOnly()) {
                items.push({
                    label: $localize`:@@myproducts:Meine Standorte`,
                    icon: 'fa-light fa-house-chimney-user fa-fw',
                    routerLink: this.currentLang + '/account/buyer/products'
                });
            }

            if (this.helper.isMasterAccount()) {
                items.push({
                    label: $localize`:@@user.subaccounts:Mitbenutzer`,
                    icon: 'fa-light fa-users fa-fw',
                    routerLink: this.currentLang + '/account/users'
                });
            }

            if (!activeContracts && this.helper.isMasterAccount()) {
                items.push({
                    label: $localize`:@@mysubscription:Jetzt Abo aktivieren!`,
                    icon: 'fa-light fa-gem fa-fw',
                    routerLink: this.currentLang + '/subscription/booking'
                });
            }


            items.push(
                {
                    label: 'Status',
                    icon: 'fa-light fa-gem fa-fw',
                    // @ts-ignore
                    styleClass: 'subscription-toggle',
                    dynamicClass: () => `subscription-toggle ${this.subscriptionService.hasSubscription ? 'active' : 'inactive'}`
                }
            )


            this.sidenav.push({
                label: 'Account',
                items
            });
        }
        if (this.helper.hasRole(Role.BUYER) && (this.currentLang === '/en' || this.currentLang === '')) {
            this.sidenav.push({
                label: $localize`:@@sidenav.help:Hilfe`,
                items: [
                    {
                        label: 'Support',
                        icon: 'fa-light fa-ticket fa-fw',
                        routerLink: 'https://support.lobster-experience.com/help/2377875478',
                        target: 'blank'
                    },
                    {
                        label: 'Info-Guide',
                        icon: 'fa-light fa-circle-info fa-fw',
                        routerLink: 'https://www.lobster-event.com/de/info-guide/7413a4e2-ebc3-396d-99c3-65db38849fdd',
                        target: 'blank'
                    }
                ]
            });
        }
        if (this.helper.hasRole(Role.BUYER) && this.currentLang === '/de') {
            this.sidenav.push({
                label: $localize`:@@sidenav.help:Hilfe`,
                items: [
                    {
                        label: 'Support',
                        icon: 'fa-light fa-ticket fa-fw',
                        routerLink: 'https://support.lobster-experience.com/help/2377875478',
                        target: 'blank'
                    },
                    {
                        label: 'Info-Guide',
                        icon: 'fa-light fa-circle-info fa-fw',
                        routerLink: 'https://www.lobster-event.com/de/info-guide/8607e6e9-8006-3542-a947-83375c8aa616',
                        target: 'blank'
                    }
                ]
            });
        }
        if (this.helper.hasRole(Role.SUPPLIER)) {
            this.sidenav.push({
                label: $localize`:@@sidenav.help:Hilfe`,
                items: [
                    {
                        label: 'Support',
                        icon: 'fa-light fa-ticket fa-fw',
                        routerLink: 'https://support.lobster-experience.com/help/2377875478',
                        target: 'blank'
                    },
                    {
                        label: 'Info-Guide',
                        icon: 'fa-light fa-circle-info fa-fw',
                        routerLink: 'https://www.lobster-event.com/de/info-guide/1ec5ad3c-4482-35d5-bc25-371f33a93ed5',
                        target: 'blank'
                    }
                ]
            });
        }
        if (this.helper.hasRole(Role.ADMINISTRATOR)) {
            this.sidenav.push({
                label: 'Admin',
                items: [
                    {
                        label: 'Login as User',
                        icon: 'fa-light fa-arrow-right-to-bracket fa-fw',
                        routerLink: this.currentLang + '/account/switch-user'
                    },
                    {
                        label: $localize`:@@navigation.overview.admin.supplier:Übersicht Suppliers`,
                        icon: 'fa-light fa-map-location-dot',
                        routerLink: this.currentLang + '/directory/s/overview'
                    },
                    {
                        label: $localize`:@@navigation.overview.admin.buyer:Übersicht Buyers`,
                        icon: 'fa-light fa-map-location-dot',
                        routerLink: this.currentLang + '/directory/b/overview'
                    },
                    {
                        label: $localize`:@@navigation.overview.admin.famtrips:Übersicht FAM Trips`,
                        icon: 'fa-regular fa-map-location-dot',
                        routerLink: this.currentLang + '/fam-trips/b/overview'
                    }
                ]
            });
        }
    }
}
